if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          const button = mediaToSwitch.querySelector('button');
          button.addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
          // Teluce: hover en el thumbnail cambia la imagen principal al instante
          // (el click sigue funcionando; hover es mouse-only, no afecta teclado/táctil).
          button.addEventListener('mouseenter', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
        this.wireSwapControls();
      }

      // Teluce: las flechas ‹ › cambian de foto con un swap (toggle is-active),
      // sin trasladar/deslizar el slider. El contador [x/n] se controla con el
      // número REAL de fotos, no con la matemática de SliderComponent (que con
      // el apilado del grid pierde el itemOffset y calcula mal los totales).
      wireSwapControls() {
        const viewer = this.elements.viewer;
        if (!viewer) return;
        viewer.update = () => {};
        viewer.initPages = () => {};
        viewer.resetPages = () => {};
        viewer.pageTotalElement = null;
        viewer.currentPageElement = null;

        const items = Array.from(viewer.querySelectorAll('.slider__slide[data-media-id]'));
        this.syncCounter(items.length, this.currentIndex(items) + 1);
        this.updateBackdrop();
        // Teluce: en mobile el swap es cambio de .is-active (crossfade), NO
        // desplazamiento. El setActiveMedia de Dawn hace scrollTo horizontal del
        // contenedor apilado, lo que sacude la imagen nueva al posicionarse.
        // Neutralizamos ese scroll para que la transición sea puro fade.
        if (!this.mql.matches) {
          const list = viewer.querySelector('.product__media-list');
          if (list) list.scrollTo = () => {};
        }
        this.wireSwipe(viewer);
      }

      // Teluce: swipe horizontal con el dedo cambia de foto; el tap sigue abriendo el zoom.
      wireSwipe(viewer) {
        let startX = null;
        let startY = null;
        let suppressClick = false;
        const threshold = 40;

        viewer.addEventListener(
          'touchstart',
          (e) => {
            suppressClick = false;
            startX = e.changedTouches[0].clientX;
            startY = e.changedTouches[0].clientY;
          },
          { passive: true }
        );

        viewer.addEventListener(
          'touchend',
          (e) => {
            if (startX === null) return;
            const dx = e.changedTouches[0].clientX - startX;
            const dy = e.changedTouches[0].clientY - startY;
            startX = startY = null;
            // tap o scroll vertical: no es swipe
            if (Math.abs(dx) < threshold || Math.abs(dx) < Math.abs(dy)) return;
            suppressClick = true;
            this.stepActiveMedia(dx < 0 ? 1 : -1);
          },
          { passive: true }
        );

        // evita que el modal de zoom se abra justo después de un swipe
        viewer.addEventListener(
          'click',
          (e) => {
            if (!suppressClick) return;
            e.preventDefault();
            e.stopPropagation();
            suppressClick = false;
          },
          true
        );
      }

      currentIndex(items) {
        const i = items.findIndex((el) => el.classList.contains('is-active'));
        return i === -1 ? 0 : i;
      }

      syncCounter(total, current) {
        const viewer = this.elements.viewer;
        const t = viewer.querySelector('.slider-counter--total');
        const c = viewer.querySelector('.slider-counter--current');
        if (t) t.textContent = total;
        if (c && current != null) c.textContent = current;
      }

      // Teluce: el fondo red-social usa la foto activa reflejada (::before con
      // filter blur). Seteamos --gallery-bg en el slider-component del viewer
      // (los ::before de mobile-list y desktop-viewer la heredan) con la URL de
      // la foto activa, para que el reflejo muestre siempre la imagen visible.
      updateBackdrop() {
        const viewer = this.elements.viewer;
        if (!viewer) return;
        const active = viewer.querySelector('.slider__slide.is-active img');
        const src = active ? active.currentSrc || active.src : '';
        viewer.style.setProperty('--gallery-bg', src ? `url("${src}")` : 'none');
      }

      stepActiveMedia(delta) {
        const items = Array.from(this.elements.viewer.querySelectorAll('.slider__slide[data-media-id]'));
        if (!items.length) return;
        const idx = (this.currentIndex(items) + delta + items.length) % items.length;
        this.setActiveMedia(items[idx].dataset.mediaId, false);
        this.syncCounter(items.length, idx + 1);
        this.updateBackdrop();
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');
        this.updateBackdrop();

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
